import { Box, Grid, Typography } from '@mui/material'
import { DateTime } from 'luxon'
import { ResponsiveContainer, ComposedChart, CartesianGrid, XAxis, YAxis, Tooltip, Line, Bar, Label } from 'recharts'
import { MetricsFieldName } from '~/generated/graphql'
import React, { useMemo } from 'react'
import { MetricsKey, MetricType } from './index'

type MetricsChartProps = {
  data: {
    name: MetricsFieldName
    value: {
      date: string
      value: number
    }[]
  }[]
  metrics: MetricType
  firstMetric: MetricsKey
  secondMetric: MetricsKey
}

type TransformedData = {
  startDate: string
  [key: string]: number | string | null // メトリクスの名前が動的にキーになる
}

export const MetricsChart: React.FC<MetricsChartProps> = ({ data, metrics, firstMetric, secondMetric }) => {
  const transformReportData = useMemo(() => {
    const dateMap: { [key: string]: TransformedData } = {}

    data.forEach((item) => {
      item.value.forEach(({ date, value }) => {
        const formattedDate = date

        if (!dateMap[formattedDate]) {
          dateMap[formattedDate] = { startDate: formattedDate }
        }

        // マイナスの値は0に変換し、元の値は保持する
        dateMap[formattedDate][item.name] = value < 0 ? 0 : value
        // ツールチップではマイナスの値をそのまま表示する
        dateMap[formattedDate][`${item.name}_TOOLTIP`] = value
      })
    })

    return Object.values(dateMap)
  }, [data])

  return (
    <Box sx={{ width: '100%', overflowX: 'scroll', overflowY: 'hidden' }}>
      <ResponsiveContainer width="100%" minWidth={1200} height={400}>
        <ComposedChart data={transformReportData}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="startDate"
            tickFormatter={(d) => DateTime.fromISO(d).toFormat('M/d')}
            tick={{ fontSize: '12px' }}
          />
          <YAxis
            yAxisId="left"
            dataKey={metrics[firstMetric]}
            tickFormatter={(val: string) => Number(val).toLocaleString()}
            tick={{ fontSize: '12px' }}
            allowDecimals={false}
          >
            <Label angle={-90} dx={-30} fontSize={12} style={{ fill: '#585858' }}>
              {`${firstMetric}${firstMetric === 'エンゲージメント率' ? '（率）' : '（回）'}`}
            </Label>
          </YAxis>
          <YAxis
            hide={!secondMetric}
            yAxisId="right"
            dataKey={metrics[secondMetric]}
            orientation="right"
            tick={{ fontSize: '12px' }}
            allowDecimals={false}
          >
            <Label angle={90} dx={30} fontSize={12} style={{ fill: '#585858' }}>
              {`${secondMetric}${secondMetric === 'エンゲージメント率' ? '（%）' : '（回）'}`}
            </Label>
          </YAxis>
          <Tooltip
            content={(props) => {
              const payloadData = props.payload?.[0]?.payload
              const firstMetricOriginalValue =
                payloadData?.[`${metrics[firstMetric]}_TOOLTIP` as keyof typeof payloadData]
              const secondMetricOriginalValue =
                payloadData?.[`${metrics[secondMetric]}_TOOLTIP` as keyof typeof payloadData]

              return (
                <Box
                  maxWidth={240}
                  sx={{
                    backgroundColor: 'white',
                    py: 1,
                    px: 2,
                    border: '1px solid lightgray',
                    borderRadius: '4px',
                  }}
                >
                  <Grid container spacing={1}>
                    <Grid size={12}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
                        {payloadData?.startDate}
                      </Typography>
                    </Grid>
                    <Grid size={10}>
                      <Typography variant="body2">{firstMetric}</Typography>
                    </Grid>
                    <Grid size={2}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {firstMetricOriginalValue}
                      </Typography>
                    </Grid>
                    <Grid size={10}>
                      <Typography variant="body2">{secondMetric}</Typography>
                    </Grid>
                    <Grid size={2}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {secondMetricOriginalValue}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )
            }}
          />
          <Line
            dataKey={metrics[firstMetric]}
            isAnimationActive={false}
            stroke={'#0073c5'}
            strokeWidth={2}
            yAxisId="left"
          />
          <Bar dataKey={metrics[secondMetric]} isAnimationActive={false} fill={'#6987ff5f'} yAxisId="right" />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  )
}
